/**
 * MDM (Master Data Management) API Client
 *
 * Unified ObjectType/ObjectRecord entity system.
 * Self-sufficient auth pattern -- singleton client, no token injection needed.
 * All snake_case -> camelCase conversion happens here.
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  PaginatedResponse,
  ObjectType,
  ObjectTypeField,
  ObjectRecord,
  NextSuffixResponse,
  CreateObjectTypeRequest,
  UpdateObjectTypeRequest,
  AddFieldRequest,
  UpdateFieldRequest,
  CreateObjectRecordRequest,
  UpdateObjectRecordRequest,
  ObjectTypeFieldType,
  FieldConfig,
  DependentsPageResponse,
} from '../types/master-data-types';

/* eslint-disable @typescript-eslint/naming-convention */
type RawPaginated<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

type RawObjectTypeField = {
  id: string;
  tenant_id: string;
  object_type_id: string;
  label: string;
  field_key: string;
  field_type: string;
  required: boolean;
  sort_order: number;
  config: Record<string, unknown> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type RawObjectType = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  prefix: string;
  display_fields: string[] | null;
  list_columns: Array<{field_key: string}> | null;
  module_tag: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  fields?: RawObjectTypeField[];
  record_count?: number;
  has_references?: boolean;
  warnings?: string[];
};

type RawObjectRecord = {
  id: string;
  tenant_id: string;
  object_type_id: string;
  code: string;
  data: Record<string, unknown>;
  display: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type RawNextSuffix = {
  suggested_suffix: string;
  prefix: string;
};

type RawDependentRow = {
  source_type: string;
  id: string;
  display_id: string;
  name: string;
  type_name: string;
  parent_type_id: string | null;
  deleted_at: string | null;
  status: string | null;
  reference_count: number;
  is_tombstone: boolean;
};

type RawDependentsPage = {
  items: RawDependentRow[];
  total: number;
  limit: number;
  offset: number;
};
/* eslint-enable @typescript-eslint/naming-convention */

export class MasterDataApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/qms/mdm';
  }

  // -- Object Types: Read ----------------------------------------------------

  async listObjectTypes(params?: {
    search?: string;
    includeArchived?: boolean;
    /**
     * Filter to ObjectTypes with this module_tag (e.g. "supplier_quality:supplier").
     * Used by seeded modules to resolve their tagged ObjectTypes by ID.
     */
    moduleTag?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<ObjectType>> {
    const qp = new URLSearchParams();
    if (params?.search) qp.set('search', params.search);
    if (params?.includeArchived) qp.set('include_archived', 'true');
    if (params?.moduleTag) qp.set('module_tag', params.moduleTag);
    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    if (params?.offset !== undefined) qp.set('offset', String(params.offset));

    const qs = qp.toString();
    const endpoint = qs
      ? `${this.basePath}/object-types?${qs}`
      : `${this.basePath}/object-types`;
    const raw = await this.get<RawPaginated<RawObjectType>>(endpoint);
    return {
      items: raw.items.map((t) => this.toObjectType(t)),
      total: raw.total,
      limit: raw.limit,
      offset: raw.offset,
    };
  }

  async getObjectType(typeId: string): Promise<ObjectType> {
    const raw = await this.get<RawObjectType>(
      `${this.basePath}/object-types/${typeId}`
    );
    return this.toObjectType(raw);
  }

  // -- Object Types: Mutations -----------------------------------------------

  async createObjectType(input: CreateObjectTypeRequest): Promise<ObjectType> {
    const raw = await this.post<RawObjectType>(
      `${this.basePath}/object-types`,
      this.toRawObjectTypeInput(input)
    );
    return this.toObjectType(raw);
  }

  async updateObjectType(
    typeId: string,
    input: UpdateObjectTypeRequest
  ): Promise<ObjectType> {
    const raw = await this.patch<RawObjectType>(
      `${this.basePath}/object-types/${typeId}`,
      this.toRawUpdateObjectTypeInput(input)
    );
    return this.toObjectType(raw);
  }

  async archiveObjectType(typeId: string): Promise<ObjectType> {
    const raw = await this.post<RawObjectType>(
      `${this.basePath}/object-types/${typeId}/archive`,
      {}
    );
    return this.toObjectType(raw);
  }

  async restoreObjectType(typeId: string): Promise<ObjectType> {
    const raw = await this.post<RawObjectType>(
      `${this.basePath}/object-types/${typeId}/restore`,
      {}
    );
    return this.toObjectType(raw);
  }

  async deleteObjectType(typeId: string): Promise<void> {
    await this.delete(`${this.basePath}/object-types/${typeId}`);
  }

  // -- Fields ----------------------------------------------------------------

  async addField(
    typeId: string,
    input: AddFieldRequest
  ): Promise<ObjectTypeField> {
    const raw = await this.post<RawObjectTypeField>(
      `${this.basePath}/object-types/${typeId}/fields`,
      this.toRawFieldInput(input)
    );
    return this.toObjectTypeField(raw);
  }

  async updateField(
    typeId: string,
    fieldId: string,
    input: UpdateFieldRequest
  ): Promise<ObjectTypeField> {
    const raw = await this.patch<RawObjectTypeField>(
      `${this.basePath}/object-types/${typeId}/fields/${fieldId}`,
      this.toRawUpdateFieldInput(input)
    );
    return this.toObjectTypeField(raw);
  }

  async deactivateField(
    typeId: string,
    fieldId: string
  ): Promise<ObjectTypeField> {
    const raw = await this.post<RawObjectTypeField>(
      `${this.basePath}/object-types/${typeId}/fields/${fieldId}/deactivate`,
      {}
    );
    return this.toObjectTypeField(raw);
  }

  async reactivateField(
    typeId: string,
    fieldId: string
  ): Promise<ObjectTypeField> {
    const raw = await this.post<RawObjectTypeField>(
      `${this.basePath}/object-types/${typeId}/fields/${fieldId}/reactivate`,
      {}
    );
    return this.toObjectTypeField(raw);
  }

  // -- Object Records: Read --------------------------------------------------

  async listObjectRecords(
    typeId: string,
    params?: {
      search?: string;
      includeArchived?: boolean;
      /**
       * JSONB field filters serialized as ``filter[field_key]=value``.
       * Each key must correspond to an active field on the ObjectType;
       * the backend rejects unknown keys with a 400.
       */
      filters?: Record<string, string>;
      /**
       * A system key (`_code`, `_createdAt`, `_updatedAt`) or an active field
       * key. The backend rejects unknown, inactive, and unsortable fields
       * with a 400. Omitted means newest-first.
       */
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    }
  ): Promise<PaginatedResponse<ObjectRecord>> {
    const qp = new URLSearchParams();
    if (params?.search) qp.set('search', params.search);
    if (params?.includeArchived) qp.set('include_archived', 'true');
    if (params?.filters) {
      for (const [fieldKey, value] of Object.entries(params.filters)) {
        qp.set(`filter[${fieldKey}]`, value);
      }
    }

    if (params?.sortBy) qp.set('sort_by', params.sortBy);
    if (params?.sortDir) qp.set('sort_dir', params.sortDir);
    if (params?.limit !== undefined) qp.set('limit', String(params.limit));
    if (params?.offset !== undefined) qp.set('offset', String(params.offset));

    const qs = qp.toString();
    const endpoint = qs
      ? `${this.basePath}/object-types/${typeId}/records?${qs}`
      : `${this.basePath}/object-types/${typeId}/records`;
    const raw = await this.get<RawPaginated<RawObjectRecord>>(endpoint);
    return {
      items: raw.items.map((r) => this.toObjectRecord(r)),
      total: raw.total,
      limit: raw.limit,
      offset: raw.offset,
    };
  }

  /**
   * Batch-list a bounded slice of records for several object types in one
   * request. Returns, per type id, the record slice plus the true total count.
   * Replaces the Library browse picker's per-type HTTP fan-out.
   */
  async batchListObjectRecords(params: {
    typeIds: string[];
    perTypeLimit?: number;
    includeArchived?: boolean;
  }): Promise<Record<string, {items: ObjectRecord[]; total: number}>> {
    if (params.typeIds.length === 0) return {};
    /* eslint-disable @typescript-eslint/naming-convention */
    const body: Record<string, unknown> = {type_ids: params.typeIds};
    if (params.perTypeLimit !== undefined)
      body.per_type_limit = params.perTypeLimit;
    if (params.includeArchived !== undefined)
      body.include_archived = params.includeArchived;
    const raw = await this.post<{
      by_type: Record<string, {items: RawObjectRecord[]; total: number}>;
    }>(`${this.basePath}/object-records/batch`, body);
    /* eslint-enable @typescript-eslint/naming-convention */
    const out: Record<string, {items: ObjectRecord[]; total: number}> = {};
    for (const [typeId, bucket] of Object.entries(raw.by_type)) {
      out[typeId] = {
        items: bucket.items.map((r) => this.toObjectRecord(r)),
        total: bucket.total,
      };
    }

    return out;
  }

  async getObjectRecord(
    typeId: string,
    recordId: string
  ): Promise<ObjectRecord> {
    const raw = await this.get<RawObjectRecord>(
      `${this.basePath}/object-types/${typeId}/records/${recordId}`
    );
    return this.toObjectRecord(raw);
  }

  // -- Object Records: Mutations ---------------------------------------------

  async createObjectRecord(
    typeId: string,
    input: CreateObjectRecordRequest
  ): Promise<ObjectRecord> {
    const payload: Record<string, unknown> = {data: input.data};
    if (input.codeSuffix) payload.code_suffix = input.codeSuffix;
    const raw = await this.post<RawObjectRecord>(
      `${this.basePath}/object-types/${typeId}/records`,
      payload
    );
    return this.toObjectRecord(raw);
  }

  async updateObjectRecord(
    typeId: string,
    recordId: string,
    input: UpdateObjectRecordRequest
  ): Promise<ObjectRecord> {
    const raw = await this.patch<RawObjectRecord>(
      `${this.basePath}/object-types/${typeId}/records/${recordId}`,
      {data: input.data}
    );
    return this.toObjectRecord(raw);
  }

  async archiveObjectRecord(
    typeId: string,
    recordId: string
  ): Promise<ObjectRecord> {
    const raw = await this.post<RawObjectRecord>(
      `${this.basePath}/object-types/${typeId}/records/${recordId}/archive`,
      {}
    );
    return this.toObjectRecord(raw);
  }

  async restoreObjectRecord(
    typeId: string,
    recordId: string
  ): Promise<ObjectRecord> {
    const raw = await this.post<RawObjectRecord>(
      `${this.basePath}/object-types/${typeId}/records/${recordId}/restore`,
      {}
    );
    return this.toObjectRecord(raw);
  }

  async deleteObjectRecord(typeId: string, recordId: string): Promise<void> {
    await this.delete(
      `${this.basePath}/object-types/${typeId}/records/${recordId}`
    );
  }

  // -- Next Suffix -----------------------------------------------------------

  async getNextSuffix(typeId: string): Promise<NextSuffixResponse> {
    const raw = await this.get<RawNextSuffix>(
      `${this.basePath}/object-types/${typeId}/next-suffix`
    );
    return {suggestedSuffix: raw.suggested_suffix, prefix: raw.prefix};
  }

  // -- Dependents -------------------------------------------------------------

  async getRecordDependentsPage(
    typeId: string,
    recordId: string,
    params?: {enrichedData?: boolean; limit?: number; offset?: number}
  ): Promise<DependentsPageResponse> {
    const query = new URLSearchParams();
    if (params?.enrichedData) query.set('enriched_data', 'true');
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    const url = `${this.basePath}/object-types/${typeId}/records/${recordId}/dependents${qs ? `?${qs}` : ''}`;
    const raw = await this.get<RawDependentsPage>(url);
    return this.toDependentsPage(raw);
  }

  // -- Private converters ----------------------------------------------------

  private toObjectTypeField(r: RawObjectTypeField): ObjectTypeField {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      objectTypeId: r.object_type_id,
      label: r.label,
      fieldKey: r.field_key,
      fieldType: r.field_type as ObjectTypeFieldType,
      required: r.required,
      sortOrder: r.sort_order,
      config: this.normalizeFieldConfig(r.config),
      isActive: r.is_active,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    };
  }

  private toObjectType(r: RawObjectType): ObjectType {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      name: r.name,
      description: r.description,
      prefix: r.prefix,
      displayFields: r.display_fields,
      listColumns:
        r.list_columns?.map((c) => ({fieldKey: c.field_key})) ?? null,
      moduleTag: r.module_tag ?? null,
      createdBy: r.created_by,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      deletedAt: r.deleted_at,
      fields: (r.fields ?? []).map((f) => this.toObjectTypeField(f)),
      recordCount: r.record_count,
      hasReferences: r.has_references,
      warnings: r.warnings ?? [],
    };
  }

  private toObjectRecord(r: RawObjectRecord): ObjectRecord {
    return {
      id: r.id,
      tenantId: r.tenant_id,
      objectTypeId: r.object_type_id,
      code: r.code,
      data: r.data,
      display: r.display ?? undefined,
      createdBy: r.created_by,
      updatedBy: r.updated_by,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      deletedAt: r.deleted_at,
    };
  }

  /* eslint-disable @typescript-eslint/naming-convention */

  /**
   * Normalize field config from backend (handles both camelCase and snake_case keys).
   */
  private normalizeFieldConfig(
    raw: Record<string, unknown> | null
  ): FieldConfig | null {
    if (!raw) return null;
    return {
      options: raw.options as string[] | undefined,
      referenceObjectTypeId: (raw.referenceObjectTypeId ??
        raw.reference_object_type_id) as string | undefined,
    };
  }

  /**
   * Convert field config to snake_case keys for the backend.
   */
  private toRawFieldConfig(
    config: Record<string, unknown>
  ): Record<string, unknown> {
    const raw: Record<string, unknown> = {};
    if (config.options !== undefined) raw.options = config.options;
    if (config.referenceObjectTypeId !== undefined)
      raw.reference_object_type_id = config.referenceObjectTypeId;
    return raw;
  }

  private toRawObjectTypeInput(
    input: CreateObjectTypeRequest
  ): Record<string, unknown> {
    return {
      name: input.name,
      prefix: input.prefix,
      ...(input.description !== undefined && {description: input.description}),
      fields: input.fields.map((f) => ({
        label: f.label,
        field_key: f.fieldKey,
        field_type: f.fieldType,
        required: f.required,
        sort_order: f.sortOrder,
        ...(f.config && {config: this.toRawFieldConfig(f.config)}),
      })),
      ...(input.displayFields && {display_fields: input.displayFields}),
    };
  }

  private toRawUpdateObjectTypeInput(
    input: UpdateObjectTypeRequest
  ): Record<string, unknown> {
    return {
      ...(input.name !== undefined && {name: input.name}),
      ...(input.description !== undefined && {description: input.description}),
      ...(input.displayFields !== undefined && {
        display_fields: input.displayFields,
      }),
      // `undefined` must stay absent from the payload: the API distinguishes
      // an omitted key (leave the layout alone) from an explicit null (clear
      // it), so collapsing the two here would make "reset" unreachable.
      ...(input.listColumns !== undefined && {
        list_columns:
          input.listColumns === null
            ? null
            : input.listColumns.map((c) => ({field_key: c.fieldKey})),
      }),
    };
  }

  private toRawFieldInput(input: AddFieldRequest): Record<string, unknown> {
    return {
      label: input.label,
      field_key: input.fieldKey,
      field_type: input.fieldType,
      required: input.required,
      sort_order: input.sortOrder,
      ...(input.config && {config: this.toRawFieldConfig(input.config)}),
    };
  }

  private toRawUpdateFieldInput(
    input: UpdateFieldRequest
  ): Record<string, unknown> {
    return {
      ...(input.label !== undefined && {label: input.label}),
      ...(input.required !== undefined && {required: input.required}),
      ...(input.config !== undefined && {
        config: input.config
          ? this.toRawFieldConfig(input.config)
          : input.config,
      }),
    };
  }

  private toDependentsPage(raw: RawDependentsPage): DependentsPageResponse {
    return {
      items: raw.items.map((r) => ({
        sourceType: r.source_type,
        id: r.id,
        displayId: r.display_id,
        name: r.name,
        typeName: r.type_name,
        parentTypeId: r.parent_type_id,
        deletedAt: r.deleted_at,
        status: r.status,
        referenceCount: r.reference_count,
        isTombstone: r.is_tombstone,
      })),
      total: raw.total,
      limit: raw.limit,
      offset: raw.offset,
    };
  }
  /* eslint-enable @typescript-eslint/naming-convention */
}

// Singleton factory
let cachedMasterDataApi: MasterDataApiClient | undefined;

export function getMasterDataApi(token?: string): MasterDataApiClient {
  if (token) return new MasterDataApiClient(token);
  cachedMasterDataApi ??= new MasterDataApiClient();
  return cachedMasterDataApi;
}
